import {
    Add,
    AlertModal,
    ArrowDown,
    ArrowUp,
    Box,
    Button,
    Caption,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownTrigger,
    Email,
    Export,
    External,
    isExpired,
    Modal,
    showToast,
    TabMenu,
    TabMenuItem,
    TextBlock,
    Upload,
    useSubPage,
} from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

import style from "./asset-custom-view-page.scss";
import { useAIPanelContext } from "components/ai-panel/AIPanelContext";
import AssetViewKebabMenu from "components/asset-management-system/asset-view-kebab-menu/AssetViewKebabMenu";
import AssetTable from "components/asset-management-system/AssetTable";
import { ManifestTab } from "components/asset-management-system/custom-views/tabs/ManifestTab";
import { OverviewTab } from "components/asset-management-system/custom-views/tabs/overview/OverviewTab";
import { ReportsTab } from "components/asset-management-system/custom-views/tabs/ReportsTab";
import { RiskTab } from "components/asset-management-system/custom-views/tabs/risk/RiskTab";
import { ValueTab } from "components/asset-management-system/custom-views/tabs/value/ValueTab";
import ExportAssetsView from "components/asset-management-system/ExportAssetsView";
import { ManageAssetView } from "components/asset-management-system/manage-assets/ManageAssetView";
import { UploadAssetsModal } from "components/asset-management-system/upload/UploadAssetsModal";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { BAM_COSMETIC_GRADING_PID, BAM_GRADING_PID } from "components/licenses/common";
import { useIsMobileViewport } from "components/navigation/hooks/useIsMobileViewport";
import { ASSET_MANAGEMENT_ROUTE, ASSET_OVERVIEW_ROUTE } from "components/router/Routes";
import { RiskLevel } from "domain/asset-management/assetFilterConstants";
import {
    AssetCustomView,
    mapCustomViewFiltersToApiFilters,
    toAssetViewScope,
    toViewFilterSelection,
} from "domain/asset-management/assets";
import { buildCosmeticGradingUrl, isMobileDevice } from "domain/asset-management/cosmeticGrading";
import {
    AUTH_ASSET_CREATE,
    AUTH_ASSET_GRADING,
    AUTH_ASSET_MANIFEST_VIEW,
    AUTH_ASSET_VIEW,
    AUTH_CUSTOM_ASSET_VIEW_VIEW,
} from "domain/authority";
import { assetViewService, Manifest } from "services/asset-management/AssetCustomViewService";
import { assetGradingService } from "services/asset-management/AssetGradingService";
import {
    Asset,
    AssetFilter,
    AssetFilters,
    AssetSearchRequest,
    DEFAULT_COLUMN_KEYS,
} from "services/asset-management/AssetService";
import { FLAG_BAM_REPORTS } from "services/feature/FeatureFlagService";
import { deduceAvailableLicenses, licenseService } from "services/licenses/LicenseService";
import { deriveTenantUuid, hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

enum Tab {
    OVERVIEW = "overview",
    INVENTORY = "inventory",
    MANIFEST = "manifest",
    RISK = "risk",
    VALUE = "value",
    REPORTS = "reports",
}

export interface Props {
    viewDetails?: AssetCustomView;
}

interface ExportRequest {
    sendEmail: boolean;
    selectedAssetIds?: string[];
    columns?: string[];
}

interface LicenseState {
    isAvailable: boolean;
    isValid: boolean;
}
const COSMETIC_GRADING_QR_CODE_SIZE_PIXELS = 200;

const AssetCustomViewPage: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const [assets, setAssets] = useState<Asset[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [totalAssetCount, setTotalAssetCount] = useState(0);
    const [activeFilters, setActiveFilters] = React.useState<AssetFilter[]>([]);
    const [searchQuery, setSearchQuery] = React.useState<string | undefined>(undefined);
    const [riskFilter, setRiskFilter] = React.useState<AssetFilters | undefined>(undefined);
    const [isUploadAssetsModalOpen, setIsUploadAssetsModalOpen] = useState(false);
    let viewTitle = t("AssetManagement.assetInventory");
    let viewDetails: AssetCustomView = {
        uuid: "",
        name: t("AssetManagement.assetInventory"),
        columns: DEFAULT_COLUMN_KEYS,
        folder: false,
        childrenViews: [],
    };

    if (props?.viewDetails) {
        viewTitle = props.viewDetails.name;
        viewDetails = props.viewDetails;
    }

    const hasFeatureLicense = userSessionService.hasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
    const hasWorkflowFeatureLicense = userSessionService.hasFeatureLicense("FEATURE_WORKFLOW_BMP");
    const hasGradingAuthority = userSessionService.userHasAllAuthorities([AUTH_ASSET_GRADING]);
    const hasViewAssetsAuthority = userSessionService.userHasAllAuthorities([AUTH_ASSET_VIEW]);
    const hasReportsFeatureFlagEnabled = useFeature(FLAG_BAM_REPORTS);
    const [exportRequest, setExportRequest] = useState<ExportRequest>({ sendEmail: false });
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>(props.viewDetails ? Tab.OVERVIEW : Tab.INVENTORY);
    const hasManifestViewAuthority = userSessionService.userHasAllAuthorities([AUTH_ASSET_MANIFEST_VIEW]);
    const { setSubPage } = useSubPage();
    const { setCurrentAssetViewScope } = useAIPanelContext();
    const [currentVisibleColumns, setCurrentVisibleColumns] = useState<string[]>(viewDetails.columns);
    const [manifest, setManifest] = useState<Manifest>();
    const [isRemoveFromGroupModalOpen, setIsRemoveFromGroupModalOpen] = useState(false);
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const [isCosmeticGradingQrModalOpen, setIsCosmeticGradingQrModalOpen] = useState(false);
    const [isRemoveFromGroupErrorModalOpen, setIsRemoveFromGroupErrorModalOpen] = useState(false);
    const assetUuidsToRemoveRef = useRef<string[]>([]);
    const customViewUuid = props.viewDetails?.uuid;
    const shouldFetchManifest = customViewUuid !== undefined && hasManifestViewAuthority;
    const [smartGradingLicenseState, setSmartGradingLicenseState] = React.useState<LicenseState>();
    const [cosmeticGradingLicenseState, setCosmeticGradingLicenseState] = React.useState<LicenseState>();
    const [isSmartGradingActive, setIsSmartGradingActive] = useState(false);
    const areViewActionsVisible = props.viewDetails === undefined || activeTab === Tab.INVENTORY;
    const isMobileViewport = useIsMobileViewport();

    const loadManifest = React.useCallback(
        (abortController?: AbortController) => {
            if (!shouldFetchManifest || customViewUuid === undefined) {
                setManifest(undefined);
                return;
            }
            assetViewService
                .fetchManifest(customViewUuid, abortController)
                .then((data) => {
                    if (!abortController?.signal.aborted) {
                        setManifest(data);
                    }
                })
                .catch((error: Error & { status?: number }) => {
                    if (abortController?.signal.aborted) {
                        return;
                    }
                    setManifest(undefined);
                    logger.error("Failed to fetch manifest for custom view:", error);
                });
        },
        [customViewUuid, shouldFetchManifest]
    );

    React.useEffect(() => {
        const abortController = new AbortController();
        loadManifest(abortController);
        return () => abortController.abort();
    }, [loadManifest]);

    React.useEffect(() => {
        const abortController = new AbortController();
        licenseService
            .fetchAllLicenses({ abortController, own: !hasTenantCookie() })
            .then((licenseList) => {
                const deducedLicenses = deduceAvailableLicenses(licenseList);
                deducedLicenses.licenses.licenseData.forEach((each) => {
                    if (each.type === BAM_GRADING_PID) {
                        setSmartGradingLicenseState({
                            isAvailable: each.available > 0,
                            isValid: !isExpired(each.expirationDate),
                        });
                    } else {
                        if (each.type === BAM_COSMETIC_GRADING_PID) {
                            setCosmeticGradingLicenseState({
                                isAvailable: each.available > 0,
                                isValid: !isExpired(each.expirationDate),
                            });
                        }
                    }
                });
            })
            .catch((reason) => {
                if (abortController.signal.aborted) {
                    return;
                }
                logger.error("Failed to fetch BAM licenses for the asset view.", reason);
            });
        return () => abortController.abort();
    }, []);

    React.useEffect(() => {
        if (!hasFeatureLicense || !hasGradingAuthority || !areViewActionsVisible) {
            return;
        }
        const abortController = new AbortController();
        const smartGradingCheck =
            customViewUuid === undefined
                ? assetGradingService.hasSmartGradingEverStarted(abortController)
                : assetGradingService.isSmartGradingEnabled(customViewUuid, abortController);
        smartGradingCheck.then(setIsSmartGradingActive).catch((error) => {
            if (abortController.signal.aborted) {
                return;
            }
            logger.error("Failed to fetch smart grading progress for the asset view.", error);
            setIsSmartGradingActive(false);
        });
        return () => abortController.abort();
    }, [customViewUuid, areViewActionsVisible]);

    React.useEffect(() => {
        if (props.viewDetails && hasFeatureLicense && hasViewAssetsAuthority) {
            setCurrentAssetViewScope(toAssetViewScope(props.viewDetails));
        }
        return () => setCurrentAssetViewScope(undefined);
    }, [props.viewDetails, hasFeatureLicense, hasViewAssetsAuthority]);

    if (!hasFeatureLicense || !hasViewAssetsAuthority) {
        return (
            <ApplicationLayout
                viewTitle={viewTitle}
                view={<div className="unauthorized-message">{t("AssetManagement.viewAssetsPermissionRequired")}</div>}
                mainRoute={ASSET_MANAGEMENT_ROUTE}
            />
        );
    }

    const handleAssetsChange = (newAssets: Asset[]) => {
        setAssets(newAssets);
    };

    const openExportModal = (request: ExportRequest) => {
        setExportRequest(request);
        setIsExportModalOpen(true);
    };

    const handleRemoveFromGroup = (assetUuids: string[]) => {
        if (!props.viewDetails?.uuid) {
            return;
        }
        assetUuidsToRemoveRef.current = assetUuids;
        setIsRemoveFromGroupModalOpen(true);
    };

    const handleConfirmRemoveFromGroup = () => {
        const groupUuid = props.viewDetails?.uuid;
        if (!groupUuid) {
            return;
        }
        setIsRemoveFromGroupModalOpen(false);
        const abortController = new AbortController();
        assetViewService
            .removeAssetsFromGroup(groupUuid, assetUuidsToRemoveRef.current, abortController)
            .then(() => {
                showToast(t("AssetManagement.removeFromGroupModal.successMessage"), "success");
                setRefreshKey((prev) => prev + 1);
            })
            .catch((error) => {
                logger.error("Failed to remove assets from group.", error);
                setIsRemoveFromGroupErrorModalOpen(true);
            });
    };

    const onViewDelete = () => {
        window.location.replace(ASSET_OVERVIEW_ROUTE.path);
    };

    const kebabMenu = props?.viewDetails && (
        <AssetViewKebabMenu
            name={viewTitle}
            viewDetails={props.viewDetails || viewDetails}
            onViewDelete={onViewDelete}
            kebabSize={"Md"}
        />
    );

    const cosmeticGradingUrl = buildCosmeticGradingUrl(deriveTenantUuid());
    const enableCosmeticGrading = () => {
        return (
            cosmeticGradingLicenseState &&
            userSessionService.userHasAllAuthorities([AUTH_ASSET_GRADING]) &&
            cosmeticGradingLicenseState.isValid &&
            isSmartGradingActive
        );
    };

    const createViewActions = () => {
        if (isMobileViewport) {
            return (
                <>
                    <DropdownMenu
                        dropdownOffset="Xxs"
                        closeOnSelection
                        flipOptions={{ fallbackPlacements: ["bottom-end", "top-start", "top-end"] }}
                        onDropdownOpenChange={setIsActionsDropdownOpen}
                    >
                        <DropdownTrigger>
                            <Box verticalAlignment="Center" gap="Xxxs">
                                <Button
                                    variant="Tertiary"
                                    icon={
                                        isActionsDropdownOpen ? (
                                            <ArrowUp size={16} color={"--theme-text-secondary"} />
                                        ) : (
                                            <ArrowDown size={16} color={"--theme-text-secondary"} />
                                        )
                                    }
                                >
                                    <Caption greyTextStyle>{t("Common.actions")}</Caption>
                                </Button>
                            </Box>
                        </DropdownTrigger>

                        <DropdownContent>
                            {isMobileViewport &&
                                activeTab !== Tab.INVENTORY &&
                                hasFeatureLicense &&
                                userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]) && (
                                    <DropdownMenuItem
                                        onClick={() =>
                                            setSubPage(
                                                <ManageAssetView
                                                    groupUuid={props.viewDetails?.uuid}
                                                    onCreateDone={() =>
                                                        setRefreshKey((previousState) => previousState + 1)
                                                    }
                                                />
                                            )
                                        }
                                        testId={testIds.workArea.asset.createAssetButton}
                                    >
                                        {t("AssetManagement.createAsset")}
                                    </DropdownMenuItem>
                                )}

                            {hasFeatureLicense && enableCosmeticGrading() && (
                                <DropdownMenuItem
                                    onClick={() => {
                                        if (!isMobileDevice()) {
                                            // Cosmetic grading needs a phone camera, so hand laptop users a QR code instead
                                            setIsCosmeticGradingQrModalOpen(true);
                                            return;
                                        }
                                        window.open(cosmeticGradingUrl, "_blank");
                                    }}
                                    testId={testIds.workArea.asset.cosmeticGradingButton}
                                >
                                    {t("AssetManagement.cosmeticGrading.buttonText")}
                                </DropdownMenuItem>
                            )}

                            {hasFeatureLicense &&
                                userSessionService.userHasAnyAuthority([
                                    AUTH_ASSET_VIEW,
                                    AUTH_CUSTOM_ASSET_VIEW_VIEW,
                                ]) && (
                                    <DropdownMenuItem
                                        onClick={() => openExportModal({ sendEmail: true })}
                                        testId={testIds.workArea.asset.emailAssetsButton}
                                    >
                                        {t("AssetManagement.sendViaEmail.title")}
                                    </DropdownMenuItem>
                                )}

                            {hasFeatureLicense &&
                                userSessionService.userHasAnyAuthority([
                                    AUTH_ASSET_VIEW,
                                    AUTH_CUSTOM_ASSET_VIEW_VIEW,
                                ]) && (
                                    <DropdownMenuItem
                                        onClick={() => openExportModal({ sendEmail: false })}
                                        testId={testIds.workArea.asset.exportAssetsButton}
                                    >
                                        {t("AssetManagement.exportAllAssets")}
                                    </DropdownMenuItem>
                                )}

                            {hasFeatureLicense && userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]) && (
                                <DropdownMenuItem
                                    onClick={() => setIsUploadAssetsModalOpen(true)}
                                    testId={testIds.workArea.asset.uploadAssetsButton}
                                >
                                    {t("AssetManagement.upload.buttonText")}
                                </DropdownMenuItem>
                            )}
                        </DropdownContent>
                    </DropdownMenu>
                    <UploadAssetsModal
                        isOpen={isUploadAssetsModalOpen}
                        onClose={() => setIsUploadAssetsModalOpen(false)}
                        groupUuid={props.viewDetails?.uuid}
                    />
                </>
            );
        }

        return (
            <>
                {hasFeatureLicense && enableCosmeticGrading() && (
                    <Button
                        icon={<External />}
                        onClick={() => {
                            if (!isMobileDevice()) {
                                // Cosmetic grading needs a phone camera, so hand laptop users a QR code instead
                                setIsCosmeticGradingQrModalOpen(true);
                                return;
                            }
                            window.open(cosmeticGradingUrl, "_blank");
                        }}
                        variant="Secondary"
                        testId={testIds.workArea.asset.cosmeticGradingButton}
                        className={style.viewActionsButtons}
                    >
                        {t("AssetManagement.cosmeticGrading.buttonText")}
                    </Button>
                )}
                {hasFeatureLicense &&
                    userSessionService.userHasAnyAuthority([AUTH_ASSET_VIEW, AUTH_CUSTOM_ASSET_VIEW_VIEW]) && (
                        <Button
                            dataType="SEND_EMAIL"
                            icon={<Email />}
                            onClick={() => openExportModal({ sendEmail: true })}
                            variant="Secondary"
                            testId={testIds.workArea.asset.emailAssetsButton}
                            className={style.viewActionsButtons}
                        >
                            {t("AssetManagement.sendViaEmail.title")}
                        </Button>
                    )}
                {hasFeatureLicense &&
                    userSessionService.userHasAnyAuthority([AUTH_ASSET_VIEW, AUTH_CUSTOM_ASSET_VIEW_VIEW]) && (
                        <Button
                            dataType="EXPORT_ALL_ASSETS"
                            icon={<Export />}
                            onClick={() => openExportModal({ sendEmail: false })}
                            variant="Secondary"
                            testId={testIds.workArea.asset.exportAssetsButton}
                            className={style.viewActionsButtons}
                        >
                            {t("AssetManagement.exportAllAssets")}
                        </Button>
                    )}
                {hasFeatureLicense && userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]) && (
                    <Button
                        icon={<Upload />}
                        onClick={() => setIsUploadAssetsModalOpen(true)}
                        variant="Secondary"
                        testId={testIds.workArea.asset.uploadAssetsButton}
                        className={style.viewActionsButtons}
                    >
                        {t("AssetManagement.upload.buttonText")}
                    </Button>
                )}
                {hasFeatureLicense && userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]) && (
                    <Button
                        testId={testIds.workArea.asset.createAssetButton}
                        variant="Primary"
                        onClick={() =>
                            setSubPage(
                                <ManageAssetView
                                    groupUuid={props.viewDetails?.uuid}
                                    onCreateDone={() => setRefreshKey((previousState) => previousState + 1)}
                                />
                            )
                        }
                        icon={<Add />}
                        className={style.viewActionsButtons}
                    >
                        {t("AssetManagement.createAsset")}
                    </Button>
                )}
                <UploadAssetsModal
                    isOpen={isUploadAssetsModalOpen}
                    onClose={() => setIsUploadAssetsModalOpen(false)}
                    groupUuid={props.viewDetails?.uuid}
                />
            </>
        );
    };

    const buildAssetSearchRequest = (): AssetSearchRequest => ({
        paths: [],
        startDate: props.viewDetails?.startDate,
        endDate: props.viewDetails?.endDate,
        ...toViewFilterSelection(
            props.viewDetails?.filters ?? [],
            props.viewDetails?.startDate,
            props.viewDetails?.endDate
        ),
        ...(props.viewDetails?.uuid && { groupUuid: props.viewDetails.uuid }),
    });

    const handleTabClick = (tab: Tab) => {
        setActiveTab(tab);
        setRiskFilter(undefined);
    };

    const handleViewDevices = (riskLevel?: RiskLevel) => {
        if (riskLevel) {
            setRiskFilter({ riskLevel: [riskLevel] });
        }
        setActiveTab(Tab.INVENTORY);
    };

    const renderAssetTable = (testId?: string) => {
        return (
            <AssetTable
                columns={viewDetails.columns}
                filters={riskFilter}
                startDate={viewDetails.startDate}
                endDate={viewDetails.endDate}
                viewId={viewDetails.uuid || undefined}
                viewFilters={viewDetails.filters}
                onAssetsChange={handleAssetsChange}
                refreshKey={refreshKey}
                onTotalCountChange={(count) => setTotalAssetCount(count)}
                onFiltersChange={(filters) => setActiveFilters(filters)}
                onSearchQueryChange={setSearchQuery}
                onExportSelected={(ids, columnKeys) =>
                    openExportModal({ sendEmail: false, selectedAssetIds: ids, columns: columnKeys })
                }
                onAssetEdit={() => setRefreshKey((prev) => prev + 1)}
                onAssetCreate={() => setRefreshKey((prev) => prev + 1)}
                onVisibleColumnsChange={(cols) => setCurrentVisibleColumns(cols)}
                toShowRemoveFromGroup={props.viewDetails !== undefined}
                onRemoveFromGroup={handleRemoveFromGroup}
                testId={testId}
            />
        );
    };
    const renderView = () => {
        const assetSearchRequest = buildAssetSearchRequest();

        if (activeTab === Tab.OVERVIEW) {
            return (
                <OverviewTab
                    onViewDevices={handleViewDevices}
                    assetSearchRequest={assetSearchRequest}
                    viewDetails={props.viewDetails}
                    refreshKey={refreshKey}
                />
            );
        } else if (activeTab === Tab.INVENTORY) {
            return renderAssetTable(testIds.workArea.asset.customView.inventory.itself);
        } else if (activeTab === Tab.MANIFEST) {
            return (
                <ManifestTab
                    customViewUuid={viewDetails.uuid}
                    viewName={viewTitle}
                    onCancel={() => setActiveTab(Tab.OVERVIEW)}
                    readOnly={true}
                    manifest={manifest}
                    onManifestRefresh={() => loadManifest()}
                />
            );
        } else if (activeTab === Tab.RISK) {
            return (
                <RiskTab
                    onViewDevices={handleViewDevices}
                    assetSearchRequest={assetSearchRequest}
                    refreshKey={refreshKey}
                />
            );
        } else if (activeTab === Tab.VALUE) {
            return (
                <ValueTab
                    assetSearchRequest={assetSearchRequest}
                    onResolveRisks={() => handleTabClick(Tab.RISK)}
                    refreshKey={refreshKey}
                    isSmartGradingLicenseAvailable={
                        smartGradingLicenseState
                            ? smartGradingLicenseState.isAvailable && smartGradingLicenseState.isValid
                            : false
                    }
                />
            );
        } else if (activeTab === Tab.REPORTS) {
            return <ReportsTab />;
        } else {
            return renderAssetTable();
        }
    };

    const view = props.viewDetails ? (
        <>
            <Box paddingBottom={"Md"}>
                <TabMenu>
                    <TabMenuItem
                        key={0}
                        isActive={activeTab === Tab.OVERVIEW}
                        onClick={() => handleTabClick(Tab.OVERVIEW)}
                        dataType="OVERVIEW"
                    >
                        {t("AssetManagement.tab.overview")}
                    </TabMenuItem>
                    <TabMenuItem
                        key={1}
                        isActive={activeTab === Tab.INVENTORY}
                        onClick={() => handleTabClick(Tab.INVENTORY)}
                        dataType="INVENTORY"
                    >
                        {t("AssetManagement.tab.inventory")}
                    </TabMenuItem>

                    {hasManifestViewAuthority && (
                        <TabMenuItem
                            key={2}
                            isActive={activeTab === Tab.MANIFEST}
                            onClick={() => handleTabClick(Tab.MANIFEST)}
                            dataType="MANIFEST"
                        >
                            {t("AssetManagement.tab.manifest")}
                        </TabMenuItem>
                    )}
                    <TabMenuItem
                        key={3}
                        isActive={activeTab === Tab.RISK}
                        onClick={() => handleTabClick(Tab.RISK)}
                        dataType="RISK"
                    >
                        {t("AssetManagement.tab.risk")}
                    </TabMenuItem>
                    {hasWorkflowFeatureLicense && hasGradingAuthority && (
                        <TabMenuItem
                            key={4}
                            isActive={activeTab === Tab.VALUE}
                            onClick={() => handleTabClick(Tab.VALUE)}
                            dataType="VALUE"
                        >
                            {t("Common.value")}
                        </TabMenuItem>
                    )}
                    {hasReportsFeatureFlagEnabled && (
                        <TabMenuItem
                            key={5}
                            isActive={activeTab === Tab.REPORTS}
                            onClick={() => handleTabClick(Tab.REPORTS)}
                            dataType="REPORTS"
                        >
                            {t("AssetManagement.tab.reports")}
                        </TabMenuItem>
                    )}
                </TabMenu>
            </Box>
            {renderView()}
        </>
    ) : (
        renderAssetTable()
    );
    return (
        <>
            <ApplicationLayout
                viewTitle={viewTitle}
                view={view}
                mainRoute={ASSET_MANAGEMENT_ROUTE}
                kebabMenu={kebabMenu}
                viewActions={areViewActionsVisible ? createViewActions() : <></>}
                enableRefresh
                onHandleRefresh={() => {
                    setRefreshKey((prev) => prev + 1);
                    if (activeTab === Tab.MANIFEST) {
                        loadManifest();
                    }
                }}
                viewActionsClassName={areViewActionsVisible ? style.viewActions : style.viewActionsHidden}
            />

            <Modal
                title={t("AssetManagement.removeFromGroup")}
                isOpen={isRemoveFromGroupModalOpen}
                hideModal={() => setIsRemoveFromGroupModalOpen(false)}
                buttons={
                    <>
                        <Button variant="Primary" onClick={handleConfirmRemoveFromGroup}>
                            {t("AssetManagement.removeFromGroup")}
                        </Button>
                        <Button variant="Secondary" onClick={() => setIsRemoveFromGroupModalOpen(false)}>
                            {t("Common.cancel")}
                        </Button>
                    </>
                }
            >
                <TextBlock>{t("AssetManagement.removeFromGroupModal.confirmationMessage")}</TextBlock>
            </Modal>

            <Modal
                title={t("AssetManagement.cosmeticGrading.qrModal.title")}
                isOpen={isCosmeticGradingQrModalOpen}
                hideModal={() => setIsCosmeticGradingQrModalOpen(false)}
                testId={testIds.workArea.asset.cosmeticGradingQrModal.itself}
                buttons={
                    <Button
                        variant="Secondary"
                        onClick={() => setIsCosmeticGradingQrModalOpen(false)}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.close")}
                    </Button>
                }
            >
                <Box columnLayout gap="Md">
                    <TextBlock>{t("AssetManagement.cosmeticGrading.qrModal.instructions")}</TextBlock>
                    <Box horizontalAlignment="Center" testId={testIds.workArea.asset.cosmeticGradingQrModal.qrCode}>
                        <QRCode value={cosmeticGradingUrl} size={COSMETIC_GRADING_QR_CODE_SIZE_PIXELS} />
                    </Box>
                </Box>
            </Modal>

            <ExportAssetsView
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                assets={assets}
                columns={exportRequest.columns ?? currentVisibleColumns}
                totalAssetCount={totalAssetCount}
                filters={
                    props.viewDetails?.filters
                        ? mapCustomViewFiltersToApiFilters(props.viewDetails.filters)
                        : activeFilters
                }
                searchQuery={searchQuery}
                selectedAssetIds={exportRequest.selectedAssetIds}
                onExportDone={() => {
                    setIsExportModalOpen(false);
                    setRefreshKey((prev) => prev + 1);
                }}
                customViewUuid={props.viewDetails?.uuid}
                sendEmail={exportRequest.sendEmail}
                manifestExists={manifest !== undefined}
            />

            <AlertModal
                title={t("Common.failure")}
                variant={"Error"}
                isOpen={isRemoveFromGroupErrorModalOpen}
                hideModal={() => setIsRemoveFromGroupErrorModalOpen(false)}
                buttons={
                    <Button variant="Primary" onClick={() => setIsRemoveFromGroupErrorModalOpen(false)}>
                        {t("Common.ok")}
                    </Button>
                }
            >
                <TextBlock>{t("AssetManagement.removeFromGroupModal.failureMessage")}</TextBlock>
            </AlertModal>
        </>
    );
};

export default AssetCustomViewPage;
